import {DOCUMENT} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';

import {catchError, of} from 'rxjs';

import {LegalConsentService} from 'frontend/services/legal_consent_service';
import {performLogout} from 'frontend/utils/logout';

/**
 * A full-viewport blocking modal component overlay that displays policy details
 * and requires user confirmation before granting application access.
 */
@Component({
  selector: 'tap-legal-consent-modal',
  templateUrl: './legal_consent_modal.ng.html',
  styleUrl: './legal_consent_modal.scss',
  imports: [MatButtonModule, MatCheckboxModule, MatDialogModule, MatProgressSpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegalConsentModal {
  private readonly legalConsentService = inject(LegalConsentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly dialogRef = inject(MatDialogRef<LegalConsentModal>, {optional: true});
  private readonly dialogData = inject<{readOnly?: boolean}>(MAT_DIALOG_DATA, {optional: true});

  readonly readOnly = input<boolean>(false);
  readonly isReadOnly = computed(() => this.dialogData?.readOnly ?? this.readOnly());
  readonly dismiss = output<void>();

  private readonly loadFailed = signal<boolean>(false);
  readonly activePolicy = toSignal(
    this.legalConsentService.getActivePolicy().pipe(
      catchError(() => {
        this.loadFailed.set(true);
        return of(undefined);
      }),
    ),
  );
  readonly accepted = signal<boolean>(false);
  readonly submitting = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);

  readonly isLoadFailed = computed(() => this.loadFailed());

  onAccept() {
    if (this.isReadOnly()) {
      return;
    }
    const policy = this.activePolicy();
    if (!policy) {
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);

    this.legalConsentService
      .acceptConsent(policy.version)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
        },
        error: (error) => {
          this.submitting.set(false);
          this.errorMessage.set(
            error?.message || 'An error occurred while saving your consent. Please try again.',
          );
        },
      });
  }

  onClose() {
    if (this.isReadOnly()) {
      if (this.dialogRef) {
        this.dialogRef.close();
      } else {
        this.dismiss.emit();
      }
      return;
    }
    // Signs out of session and redirects to SSO login.
    performLogout();
  }
}
